import { useMemo } from "react"
import { isEmpty } from "lodash"
import { VIEW_TYPES } from "helpers/base"
import { combineTanStackStatuses, DataStatus } from "helpers/data-statuses"
import { computeAddedFilters } from "helpers/filters"
import { computeFilterOns, FilterOn, MembershipFilterOn } from "helpers/filters/filter-ons"
import { Normalization } from "connected-components/dashboard/helpers"
import { Filter, FilterSecurity } from "connected-components/dashboard/context/types"
import { IsoDate } from "helpers/types/dates"
import { SelectedTypedInstrument } from "helpers/types/instruments/selected"
import { useAssetFilters, useSecuritiesDescriptors } from "resources/filters"
import { useTagCategories } from "resources/tags"
import { useAttributeCategories } from "resources/attributes"
import { useClassificationsData } from "resources/classifications"
import useFilterSecuritiesData from "hooks/use-filter-securities-data"
import { computeExperimentSegmentTooltip, computePickerFilterFromSegment } from "../helpers/segment-filters"

const EMPTY_ARRAY: FilterOn[] = []

function isMembershipFilterOn(filterOn: FilterOn): filterOn is MembershipFilterOn {
  return filterOn.type === "tag" || filterOn.type === "positionAttributes"
}

// Applied here, not in computeFilterOns, so the dashboard and Analyze Trends keep the generic tooltip
function applyExperimentSegmentTooltip(filterOns: FilterOn[], isActive: boolean, isExperimentOfOwnPortfolio: boolean, baseName: string | null): FilterOn[] {
  if (!isActive) {
    return filterOns
  }

  const tooltip = computeExperimentSegmentTooltip(isExperimentOfOwnPortfolio, baseName)
  return filterOns.map(filterOn => {
    if (!isMembershipFilterOn(filterOn) || !filterOn.isUnsupported) {
      return filterOn
    }

    return {
      ...filterOn,
      tooltip,
      options: filterOn.options.map(option => (option.isUnsupported ? { ...option, tooltip } : option)),
    }
  })
}

export default function useSegmentFilterOptions(
  account: string,
  modelId: string | null,
  instrument: SelectedTypedInstrument,
  currentDate: IsoDate,
  filter: Filter | null,
  normalization: Normalization,
  isActive: boolean,
  isExperimentOfOwnPortfolio = false,
  baseName: string | null = null,
): { filterOns: FilterOn[], addedFilters: unknown[], securityIds: { modelProviderIds: string[], swapTickers: string[] }, status: DataStatus } {
  const [assetFilters, assetFiltersStatus] = useAssetFilters(modelId, account, instrument, currentDate, currentDate, false)

  const pickerFilter = useMemo(() => computePickerFilterFromSegment(filter), [filter])

  const securitiesIds = useMemo(() => Object.values(pickerFilter?.securities || {}).reduce((acc: string[], filterTypeSecurities: FilterSecurity[]) =>
    [...acc, ...filterTypeSecurities.map(({ ticker, mic, country }) => `${ticker}.${mic || country}`)], []),
  [pickerFilter])

  const [filterSecurities, filterSecuritiesStatus] = useFilterSecuritiesData(modelId, account, instrument, undefined, undefined, currentDate, false)
  const [tagCategories, tagCategoriesStatus] = useTagCategories(instrument)
  const [attributeCategories, attributeCategoriesStatus] = useAttributeCategories(instrument)
  const [securitiesDescriptors, securitiesDescriptorsStatus] = useSecuritiesDescriptors(account, modelId, securitiesIds, currentDate)

  const {
    classifications,
    classificationsValues,
    flattenedClassificationsValues,
    status: classificationsStatus,
  } = useClassificationsData(currentDate, pickerFilter ?? undefined)

  const filterOns = useMemo(() => {
    if (isEmpty(assetFilters)) {
      return EMPTY_ARRAY
    }

    // [TODO:TS] Fix when typing helpers/base.js
    const viewType = (isActive ? VIEW_TYPES.ACTIVE : VIEW_TYPES.ABSOLUTE) as "absolute" | "active"
    const computedFilterOns = computeFilterOns(
      viewType,
      assetFilters,
      pickerFilter ?? undefined,
      undefined,
      tagCategories,
      classifications?.map(({ versions, ...classification }) => ({ ...classification, version: versions[versions.length - 1] })) || [],
      classificationsValues,
      attributeCategories,
      instrument?.type,
      normalization,
      false,
      false,
      undefined,
    )
    return applyExperimentSegmentTooltip(computedFilterOns, isActive, isExperimentOfOwnPortfolio, baseName)
  }, [assetFilters, pickerFilter, tagCategories, classifications, classificationsValues, attributeCategories, instrument?.type, normalization, isActive, isExperimentOfOwnPortfolio, baseName])

  const addedFilters = useMemo(() => computeAddedFilters(pickerFilter, filterOns, {
    tagCategories,
    securitiesDescriptors,
    attributeCategories,
    classificationsInfos: { classificationsMetas: classifications, classificationsValues: flattenedClassificationsValues },
    portfolioFilters: assetFilters,
  }), [pickerFilter, filterOns, tagCategories, classifications, securitiesDescriptors, assetFilters, attributeCategories, flattenedClassificationsValues])

  const status = combineTanStackStatuses([
    assetFiltersStatus,
    filterSecuritiesStatus,
    tagCategoriesStatus,
    attributeCategoriesStatus,
    securitiesDescriptorsStatus,
    classificationsStatus,
  ])

  return { filterOns, addedFilters, securityIds: filterSecurities, status }
}
